// Shared custom dropdown: a button + dark floating listbox that replaces a native <select> so the
// OPEN popup is themable (macOS native <select> popups cannot be styled — see WEB-DESIGN-SYSTEM.md §4a).
// Drop-in for our simple "pick one value" selects (Teams dimensions, Account role/homepage). Keyboard:
// Enter/Space/ArrowDown open; Up/Down move a highlight WITHOUT committing; Enter/Space commit the
// highlighted option; Escape closes. onChange fires only on an actual commit (click or Enter), never
// on mere arrow navigation — these selects write to the API, so a keystroke must not.
// Options may include group headers ({ header: true }): rendered as a non-interactive label, skipped
// by keyboard nav and never matched as a value.

export interface CSOption {
  id: string;
  name: string;
  header?: boolean; // a non-selectable group label
}

export interface CSInstance {
  /** The element to insert into the DOM (carries the class(es) you passed). */
  readonly root: HTMLElement;
  /** Current selected value ('' when the placeholder/none option is selected). */
  value: string;
  /** Replace the option list and set the selected value (used when the source list changes). */
  setOptions(options: CSOption[], value: string): void;
  /** Move the selection without changing the options. */
  setValue(value: string): void;
  /** Enable/disable interaction. */
  setDisabled(disabled: boolean): void;
}

interface CSConfig {
  value: string;
  options: CSOption[];
  ariaLabel: string;
  className?: string; // extra classes on the root (e.g. 'tms-select' so existing selectors still match)
  placeholder?: string; // label shown when value has no matching option
  disabled?: boolean;
  onChange: (id: string) => void;
}

const selectable = (opts: CSOption[]): CSOption[] => opts.filter((o) => !o.header);

export function customSelect(config: CSConfig): CSInstance {
  let options = config.options.slice();
  let value = config.value;
  let disabled = !!config.disabled;
  let active = -1; // index into `options` of the keyboard-highlighted (not yet committed) row

  const root = document.createElement('div');
  root.className = 'dsel' + (config.className ? ' ' + config.className : '');

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'dsel-btn';
  btn.setAttribute('aria-haspopup', 'listbox');
  btn.setAttribute('aria-expanded', 'false');
  btn.setAttribute('aria-label', config.ariaLabel);

  const labelEl = document.createElement('span');
  labelEl.className = 'dsel-label';
  const caret = document.createElement('span');
  caret.className = 'dsel-caret';
  caret.setAttribute('aria-hidden', 'true');
  caret.textContent = '▾';
  btn.append(labelEl, caret);

  const panel = document.createElement('ul');
  panel.className = 'dsel-panel';
  panel.setAttribute('role', 'listbox');
  panel.hidden = true;

  root.append(btn, panel);

  const labelFor = (v: string): string => {
    const o = options.find((x) => !x.header && x.id === v);
    return o ? o.name : (config.placeholder ?? 'Select');
  };
  const renderLabel = (): void => { labelEl.textContent = labelFor(value); };

  const renderPanel = (): void => {
    panel.textContent = '';
    options.forEach((o, i) => {
      if (o.header) {
        const head = document.createElement('li');
        head.className = 'dsel-head';
        head.setAttribute('aria-hidden', 'true');
        head.textContent = o.name;
        panel.appendChild(head);
        return;
      }
      const li = document.createElement('li');
      li.className = 'dsel-opt' + (o.id === value ? ' is-sel' : '') + (i === active ? ' is-active' : '');
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', o.id === value ? 'true' : 'false');
      li.textContent = o.name;
      li.addEventListener('click', () => commit(i));
      panel.appendChild(li);
    });
  };

  const highlight = (): void => {
    const opts = Array.from(panel.querySelectorAll('.dsel-opt'));
    let vi = 0;
    options.forEach((o, i) => { if (!o.header) { if (i === active) opts[vi]?.classList.add('is-active'); else opts[vi]?.classList.remove('is-active'); vi += 1; } });
    if (active >= 0) {
      let visIdx = 0;
      for (let i = 0; i < options.length; i++) { if (options[i]?.header) continue; if (i === active) break; visIdx += 1; }
      (opts[visIdx] as HTMLElement | undefined)?.scrollIntoView({ block: 'nearest' });
    }
  };

  function commit(idx: number): void {
    const o = options[idx];
    close();
    btn.focus();
    if (o && !o.header && o.id !== value) { value = o.id; renderLabel(); config.onChange(o.id); }
  }

  const firstSelectable = (): number => options.findIndex((o) => !o.header);
  const step = (from: number, dir: 1 | -1): number => {
    let i = from;
    for (let n = 0; n < options.length; n++) {
      i += dir;
      if (i < 0 || i >= options.length) return from < 0 ? firstSelectable() : from;
      if (!options[i]?.header) return i;
    }
    return from;
  };

  const onOutside = (e: MouseEvent): void => { if (!root.contains(e.target as Node)) close(); };
  const onKeyDoc = (e: KeyboardEvent): void => { if (e.key === 'Escape') { close(); btn.focus(); } };

  function open(): void {
    if (disabled || !selectable(options).length) return;
    active = options.findIndex((o) => !o.header && o.id === value);
    if (active < 0) active = firstSelectable();
    renderPanel();
    panel.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    highlight();
    document.addEventListener('mousedown', onOutside, true);
    document.addEventListener('keydown', onKeyDoc);
  }
  function close(): void {
    panel.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('mousedown', onOutside, true);
    document.removeEventListener('keydown', onKeyDoc);
  }

  btn.addEventListener('click', () => { if (panel.hidden) open(); else close(); });
  btn.addEventListener('keydown', (e) => {
    if (panel.hidden) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); active = step(active, 1); highlight(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); active = step(active, -1); highlight(); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (active >= 0) commit(active); }
  });

  const applyDisabled = (): void => {
    btn.disabled = disabled;
    root.classList.toggle('is-disabled', disabled);
  };

  renderLabel();
  applyDisabled();

  return {
    root,
    get value() { return value; },
    set value(v: string) { value = v; renderLabel(); },
    setOptions(next: CSOption[], v: string): void {
      options = next.slice();
      value = v;
      renderLabel();
      if (!panel.hidden) { active = options.findIndex((o) => !o.header && o.id === value); renderPanel(); highlight(); }
    },
    setValue(v: string): void {
      value = v;
      renderLabel();
      if (!panel.hidden) renderPanel();
    },
    setDisabled(d: boolean): void {
      disabled = d;
      applyDisabled();
      if (d) close();
    },
  };
}
